import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import {
  openVerifiedMach1Installation,
  type ModelInstallProgress,
  type VerifiedMach1Installation,
} from "./browser-mach1-store";
import {
  assertExactLaunchDisposed,
  disposeExactLaunch,
  exactLaunchOptions,
  loadExactRuntimeBundle,
  type ExactAssetRef,
  type ExactOfflineLaunchedApp,
  type ExactOnlineCheckpoint,
  type LoadedExactRuntimeBundle,
  type ResumablePhysicalPhase1Descriptor,
  type ResumablePhysicalPhase1Envelope,
  type ResumedPhysicalAcceptanceResult,
} from "./exact-runtime-loader";
import { PHYSICAL_RUN_ID_PATTERN } from "./physical-run-id";

const PHASE_ROOT = "prime-mach1-site-physical-pages";
const SHA256 = /^[0-9a-f]{64}$/u;
const NONCE = /^[0-9a-f]{32}$/u;

export interface DocumentStartObservation {
  capturedAt: string;
  pageInstanceId: string;
  documentUrl: string;
  performanceTimeOrigin: number;
  navigationEntries: NavigationEntryObservation[];
  crossOriginIsolated: boolean;
  navigatorOnline: boolean;
  serviceWorkerController: { scriptUrl: string; state: ServiceWorkerState } | null;
}

const bootstrap = (globalThis as typeof globalThis & {
  __PRIME_PAGE_BOOTSTRAP__?: DocumentStartObservation;
}).__PRIME_PAGE_BOOTSTRAP__;
export const SITE_PAGE_INSTANCE_ID = bootstrap?.pageInstanceId ?? crypto.randomUUID();

export interface NavigationEntryObservation {
  entryType: string;
  name: string;
  startTime: number;
  duration: number;
  type?: string;
  domComplete?: number;
  loadEventEnd?: number;
}

export interface RawPageObservation {
  capturedAt: string;
  sitePageInstanceId: string;
  documentUrl: string;
  performanceTimeOrigin: number;
  navigationEntries: NavigationEntryObservation[];
  documentStart: DocumentStartObservation | null;
  crossOriginIsolated: boolean;
  navigatorOnline: boolean;
  serviceWorker: {
    controlled: boolean;
    scriptUrl: string | null;
    state: ServiceWorkerState | null;
    buildId: string;
    scriptByteCount: number;
    scriptSha256: string;
    appManifestByteCount: number;
    appManifestSha256: string;
  };
}

export interface RawServiceWorkerPhysicalAuditEvent {
  seq: number;
  type:
    | "physical_armed"
    | "pagehide_observed"
    | "origin_lockdown_activated"
    | "phase2_bound"
    | "cache_hit"
    | "cache_miss_blocked"
    | "request_blocked"
    | "audit_sealed"
    | "audit_aborted";
  observedAt: string;
  serviceWorkerNow: number;
  [key: string]: unknown;
}

export interface RawServiceWorkerPhysicalAudit {
  kind: "prime-mach1-service-worker-physical-audit";
  schemaVersion: 1;
  nonce: string;
  descriptorSha256: string;
  serviceWorker: {
    buildId: string;
    scriptUrl: string;
    registrationScope: string;
    activeState: string | null;
  };
  armedBy: {
    sourceClientId: string | null;
    sourceClientUrl: string | null;
    pageInstanceId: string;
    performanceTimeOrigin: number;
  };
  controlledWindowsAtArm: Array<{ id: string; url: string }>;
  events: RawServiceWorkerPhysicalAuditEvent[];
  seal: null | {
    kind: "prime-mach1-service-worker-audit-seal";
    schemaVersion: 1;
    sealedAt: string;
    eventCount: number;
    preSealByteCount: number;
    preSealSha256: string;
  };
  abort: null | {
    kind: "prime-mach1-service-worker-audit-abort";
    schemaVersion: 1;
    abortedAt: string;
    reason: string;
    eventCount: number;
  };
}

export interface SealedPhysicalServiceWorkerAudit {
  audit: RawServiceWorkerPhysicalAudit & {
    seal: NonNullable<RawServiceWorkerPhysicalAudit["seal"]>;
    abort: null;
  };
  auditBytes: Uint8Array;
  auditByteCount: number;
  auditSha256: string;
}

export interface SiteAcceptanceCheckpoint {
  kind: "prime-mach1-resumable-physical-phase1-envelope";
  byteCount: number;
  sha256: string;
  bytesBase64: string;
}

export interface SitePhysicalPhase1Descriptor {
  kind: "prime-mach1-site-physical-phase1";
  schemaVersion: 1;
  nonce: string;
  runId: string;
  hostInvocationId: string;
  createdAt: string;
  runtimeManifestSha256: string;
  modelFileInventory: Array<{ name: string; size: number; lastModified: number }>;
  page: RawPageObservation;
  exactCheckpoint: ExactOnlineCheckpoint;
  acceptanceCheckpoint: SiteAcceptanceCheckpoint;
}

export interface SitePhysicalCommitMarker {
  kind: "prime-mach1-site-physical-phase1-commit";
  schemaVersion: 1;
  nonce: string;
  runId: string;
  descriptorByteCount: number;
  descriptorSha256: string;
  acceptanceCheckpointByteCount: number;
  acceptanceCheckpointSha256: string;
  committedAt: string;
}

export interface CommittedSitePhysicalPhase1 {
  descriptor: SitePhysicalPhase1Descriptor;
  marker: SitePhysicalCommitMarker;
  descriptorBytes: Uint8Array;
  descriptorSha256: string;
  markerBytes: Uint8Array;
  markerSha256: string;
  envelope: ResumablePhysicalPhase1Envelope;
  acceptance: ResumablePhysicalPhase1Descriptor;
}

export type CommittedSitePhysicalIdentity = Pick<
  CommittedSitePhysicalPhase1,
  | "descriptor"
  | "marker"
  | "descriptorBytes"
  | "descriptorSha256"
  | "markerBytes"
  | "markerSha256"
  | "envelope"
>;

export interface CreatedResumablePhysicalPhase1 extends CommittedSitePhysicalPhase1 {
  bundle: LoadedExactRuntimeBundle;
  installation: VerifiedMach1Installation;
}

export interface ResumedSitePhysicalAcceptance {
  committed: CommittedSitePhysicalPhase1;
  page: RawPageObservation;
  bundle: LoadedExactRuntimeBundle;
  phase2: ExactOfflineLaunchedApp["phase2"];
  result: ResumedPhysicalAcceptanceResult;
}

interface SwReply {
  ok?: boolean;
  error?: string;
  buildId?: string;
  audit?: unknown;
  active?: null | {
    marker: { nonce: string; descriptorSha256: string; buildId: string };
    terminal: "active" | "sealed" | "aborted";
  };
  auditBytes?: Uint8Array;
  auditByteCount?: number;
  auditSha256?: string;
}

function navigationEntries(): NavigationEntryObservation[] {
  return performance.getEntriesByType("navigation").map((entry) => {
    const navigation = entry as PerformanceNavigationTiming;
    return {
      entryType: entry.entryType,
      name: entry.name,
      startTime: entry.startTime,
      duration: entry.duration,
      type: navigation.type,
      domComplete: navigation.domComplete,
      loadEventEnd: navigation.loadEventEnd,
    };
  });
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.byteLength; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, Math.min(bytes.byteLength, offset + 0x8000)));
  }
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  if (typeof value !== "string" || value.length === 0 || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/u.test(value)) {
    throw new Error("Physical acceptance checkpoint is not canonical base64");
  }
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (base64(bytes) !== value) throw new Error("Physical acceptance checkpoint base64 is not canonical");
  return bytes;
}

async function exactResponseBytes(pathname: string): Promise<Uint8Array> {
  const response = await fetch(pathname, { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) throw new Error(`${pathname} identity fetch failed (${response.status})`);
  return new Uint8Array(await response.arrayBuffer());
}

export async function observeCurrentPage(expectedBuildId: string): Promise<RawPageObservation> {
  if (!SHA256.test(expectedBuildId)) throw new Error("Page observation requires a hash-bound SW build id");
  const controller = navigator.serviceWorker?.controller ?? null;
  const [workerBytes, appManifestBytes] = await Promise.all([
    exactResponseBytes("/sw.js"),
    exactResponseBytes("/runtime/app-assets.json"),
  ]);
  const appManifest = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(appManifestBytes)) as {
    cacheBuildId?: string;
    serviceWorker?: { path?: string; buildId?: string; byteCount?: number; sha256?: string };
  };
  const workerSha256 = bytesToHex(sha256(workerBytes));
  if (
    appManifest.cacheBuildId !== expectedBuildId ||
    appManifest.serviceWorker?.path !== "/sw.js" ||
    appManifest.serviceWorker.buildId !== expectedBuildId ||
    appManifest.serviceWorker.byteCount !== workerBytes.byteLength ||
    appManifest.serviceWorker.sha256 !== workerSha256
  ) throw new Error("Raw service worker bytes do not match the active app build manifest");
  return {
    capturedAt: new Date().toISOString(),
    sitePageInstanceId: SITE_PAGE_INSTANCE_ID,
    documentUrl: location.href,
    performanceTimeOrigin: performance.timeOrigin,
    navigationEntries: navigationEntries(),
    documentStart: bootstrap ? structuredClone(bootstrap) : null,
    crossOriginIsolated: globalThis.crossOriginIsolated === true,
    navigatorOnline: navigator.onLine,
    serviceWorker: {
      controlled: controller !== null,
      scriptUrl: controller?.scriptURL ?? null,
      state: controller?.state ?? null,
      buildId: expectedBuildId,
      scriptByteCount: workerBytes.byteLength,
      scriptSha256: workerSha256,
      appManifestByteCount: appManifestBytes.byteLength,
      appManifestSha256: bytesToHex(sha256(appManifestBytes)),
    },
  };
}

async function phaseDirectory(create: boolean): Promise<FileSystemDirectoryHandle> {
  if (!navigator.storage?.getDirectory) throw new Error("OPFS is required for the physical page handoff");
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(PHASE_ROOT, { create });
}

async function writeClosedFile(
  directory: FileSystemDirectoryHandle,
  name: string,
  bytes: Uint8Array,
): Promise<void> {
  const handle = await directory.getFileHandle(name, { create: true });
  const writable = await handle.createWritable({ keepExistingData: false });
  try {
    await writable.write(Uint8Array.from(bytes));
    await writable.close();
  } catch (error) {
    await writable.abort(error).catch(() => undefined);
    throw error;
  }
}

async function persistDescriptor(
  descriptor: SitePhysicalPhase1Descriptor,
): Promise<{
  marker: SitePhysicalCommitMarker;
  descriptorBytes: Uint8Array;
  descriptorSha256: string;
  markerBytes: Uint8Array;
  markerSha256: string;
}> {
  const directory = await phaseDirectory(true);
  const descriptorBytes = new TextEncoder().encode(JSON.stringify(descriptor));
  const descriptorSha256 = bytesToHex(sha256(descriptorBytes));
  await writeClosedFile(directory, `${descriptor.nonce}.phase1.json`, descriptorBytes);
  const marker: SitePhysicalCommitMarker = {
    kind: "prime-mach1-site-physical-phase1-commit",
    schemaVersion: 1,
    nonce: descriptor.nonce,
    runId: descriptor.runId,
    descriptorByteCount: descriptorBytes.byteLength,
    descriptorSha256,
    acceptanceCheckpointByteCount: descriptor.acceptanceCheckpoint.byteCount,
    acceptanceCheckpointSha256: descriptor.acceptanceCheckpoint.sha256,
    committedAt: new Date().toISOString(),
  };
  const markerBytes = new TextEncoder().encode(JSON.stringify(marker));
  // This exact marker is the atomic visibility boundary and is written last.
  await writeClosedFile(directory, `${descriptor.nonce}.commit.json`, markerBytes);
  return {
    marker,
    descriptorBytes,
    descriptorSha256,
    markerBytes,
    markerSha256: bytesToHex(sha256(markerBytes)),
  };
}

export async function readCommittedSitePhysicalIdentity(
  nonce: string,
): Promise<CommittedSitePhysicalIdentity> {
  if (!NONCE.test(nonce)) throw new Error("Physical phase nonce is invalid");
  const directory = await phaseDirectory(false);
  const markerFile = await directory.getFileHandle(`${nonce}.commit.json`).then((handle) => handle.getFile());
  const markerBytes = new Uint8Array(await markerFile.arrayBuffer());
  const marker = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(markerBytes)) as SitePhysicalCommitMarker;
  if (
    marker.kind !== "prime-mach1-site-physical-phase1-commit" ||
    marker.schemaVersion !== 1 ||
    marker.nonce !== nonce ||
    !PHYSICAL_RUN_ID_PATTERN.test(marker.runId) ||
    !Number.isSafeInteger(marker.descriptorByteCount) || marker.descriptorByteCount <= 0 ||
    !SHA256.test(marker.descriptorSha256) ||
    !Number.isSafeInteger(marker.acceptanceCheckpointByteCount) || marker.acceptanceCheckpointByteCount <= 0 ||
    !SHA256.test(marker.acceptanceCheckpointSha256)
  ) throw new Error("Physical phase commit marker is invalid");

  const descriptorFile = await directory.getFileHandle(`${nonce}.phase1.json`).then((handle) => handle.getFile());
  const descriptorBytes = new Uint8Array(await descriptorFile.arrayBuffer());
  if (
    descriptorBytes.byteLength !== marker.descriptorByteCount ||
    bytesToHex(sha256(descriptorBytes)) !== marker.descriptorSha256
  ) throw new Error("Physical phase descriptor does not match its atomic commit marker");
  const descriptor = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(descriptorBytes)) as SitePhysicalPhase1Descriptor;
  if (
    descriptor.kind !== "prime-mach1-site-physical-phase1" ||
    descriptor.schemaVersion !== 1 ||
    descriptor.nonce !== nonce ||
    descriptor.runId !== marker.runId ||
    !SHA256.test(descriptor.runtimeManifestSha256) ||
    descriptor.modelFileInventory?.length !== 18 ||
    descriptor.acceptanceCheckpoint?.kind !== "prime-mach1-resumable-physical-phase1-envelope" ||
    descriptor.acceptanceCheckpoint.byteCount !== marker.acceptanceCheckpointByteCount ||
    descriptor.acceptanceCheckpoint.sha256 !== marker.acceptanceCheckpointSha256
  ) throw new Error("Committed physical phase descriptor has an invalid identity");
  const checkpointBytes = fromBase64(descriptor.acceptanceCheckpoint.bytesBase64);
  if (
    checkpointBytes.byteLength !== descriptor.acceptanceCheckpoint.byteCount ||
    bytesToHex(sha256(checkpointBytes)) !== descriptor.acceptanceCheckpoint.sha256
  ) throw new Error("Committed inner resumable checkpoint bytes/hash changed");
  const envelope: ResumablePhysicalPhase1Envelope = {
    kind: "prime-mach1-resumable-physical-phase1-envelope",
    schemaVersion: 1,
    descriptorBytes: checkpointBytes,
    descriptorByteCount: checkpointBytes.byteLength,
    descriptorSha256: descriptor.acceptanceCheckpoint.sha256,
  };
  return {
    descriptor,
    marker,
    descriptorBytes,
    descriptorSha256: marker.descriptorSha256,
    markerBytes,
    markerSha256: bytesToHex(sha256(markerBytes)),
    envelope,
  };
}

export async function readCommittedPhysicalPhase(
  nonce: string,
  bundle?: LoadedExactRuntimeBundle,
): Promise<CommittedSitePhysicalPhase1> {
  const identity = await readCommittedSitePhysicalIdentity(nonce);
  const runtimeBundle = bundle ?? await loadExactRuntimeBundle(await openVerifiedMach1Installation());
  const acceptance = await runtimeBundle.module.parseResumablePhysicalPhase1(identity.envelope);
  if (
    acceptance.runId !== identity.descriptor.runId ||
    JSON.stringify(acceptance.exactCheckpoint) !== JSON.stringify(identity.descriptor.exactCheckpoint)
  ) throw new Error("Outer Site descriptor changed the inner exact checkpoint/run identity");
  return { ...identity, acceptance };
}

function assertRawSwAudit(
  value: unknown,
  nonce: string,
  descriptorSha256: string,
  buildId: string,
): asserts value is RawServiceWorkerPhysicalAudit {
  const audit = value as RawServiceWorkerPhysicalAudit;
  if (
    audit?.kind !== "prime-mach1-service-worker-physical-audit" ||
    audit.schemaVersion !== 1 ||
    audit.nonce !== nonce ||
    audit.descriptorSha256 !== descriptorSha256 ||
    audit.serviceWorker?.buildId !== buildId ||
    !Array.isArray(audit.controlledWindowsAtArm) ||
    !Array.isArray(audit.events) ||
    audit.events.some((event, index) => event.seq !== index + 1 || typeof event.type !== "string") ||
    (audit.seal !== null && audit.abort !== null)
  ) throw new Error("Raw service-worker physical audit failed its source identity envelope");
}

export async function swMessage(data: Record<string, unknown>, timeoutMs = 15_000): Promise<SwReply> {
  const controller = navigator.serviceWorker?.controller;
  if (!controller) throw new Error("Physical handoff requires an active service worker controller");
  const channel = new MessageChannel();
  const reply = new Promise<SwReply>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Service worker physical audit timed out")), timeoutMs);
    channel.port1.onmessage = (event: MessageEvent<SwReply>) => {
      clearTimeout(timeout);
      resolve(event.data);
    };
  });
  controller.postMessage(data, [channel.port2]);
  const value = await reply;
  if (value.ok !== true) throw new Error(value.error ?? "Service worker rejected physical handoff");
  return value;
}

export async function snapshotPhysicalServiceWorkerAudit(
  nonce: string,
  descriptorSha256: string,
  buildId: string,
): Promise<RawServiceWorkerPhysicalAudit> {
  const reply = await swMessage({ type: "prime-mach1-physical-audit", nonce });
  assertRawSwAudit(reply.audit, nonce, descriptorSha256, buildId);
  return reply.audit;
}

export async function sealPhysicalServiceWorkerAudit(
  nonce: string,
  descriptorSha256: string,
  buildId: string,
): Promise<SealedPhysicalServiceWorkerAudit> {
  const reply = await swMessage({ type: "prime-mach1-physical-seal", nonce }, 120_000);
  assertRawSwAudit(reply.audit, nonce, descriptorSha256, buildId);
  if (
    !reply.audit.seal || reply.audit.abort !== null ||
    !(reply.auditBytes instanceof Uint8Array) ||
    reply.auditByteCount !== reply.auditBytes.byteLength ||
    !SHA256.test(reply.auditSha256 ?? "") ||
    bytesToHex(sha256(reply.auditBytes)) !== reply.auditSha256
  ) throw new Error("Service-worker audit seal bytes/hash are incomplete");
  return {
    audit: reply.audit as SealedPhysicalServiceWorkerAudit["audit"],
    auditBytes: reply.auditBytes,
    auditByteCount: reply.auditByteCount,
    auditSha256: reply.auditSha256!,
  };
}

export async function physicalServiceWorkerStatus(): Promise<SwReply["active"]> {
  return (await swMessage({ type: "prime-mach1-physical-status" })).active ?? null;
}

export async function abortPhysicalServiceWorkerAudit(nonce: string, reason: string): Promise<void> {
  await swMessage({ type: "prime-mach1-physical-abort", nonce, reason });
}

export async function disarmPhysicalServiceWorkerAudit(nonce: string): Promise<void> {
  const active = await physicalServiceWorkerStatus();
  if (!active) return;
  if (active.marker.nonce !== nonce) throw new Error("A different physical lockdown nonce is active");
  if (active.terminal !== "sealed") throw new Error("Only a sealed physical lockdown can be disarmed");
  await swMessage({ type: "prime-mach1-physical-disarm", nonce });
}

function pagehideSender(nonce: string, descriptorSha256: string): () => void {
  const send = (event: PageTransitionEvent) => {
    navigator.serviceWorker.controller?.postMessage({
      type: "prime-mach1-physical-pagehide",
      nonce,
      descriptorSha256,
      observation: {
        eventType: event.type,
        persisted: event.persisted,
        eventTimeStamp: event.timeStamp,
        visibilityState: document.visibilityState,
        pageInstanceId: SITE_PAGE_INSTANCE_ID,
        performanceTimeOrigin: performance.timeOrigin,
        observedAt: new Date().toISOString(),
      },
    });
  };
  globalThis.addEventListener("pagehide", send, { once: true });
  return () => globalThis.removeEventListener("pagehide", send);
}

export async function createResumableSitePhysicalPhase1(
  installation: VerifiedMach1Installation,
  bundle: LoadedExactRuntimeBundle,
  hostInvocationId: string,
): Promise<CreatedResumablePhysicalPhase1> {
  if (navigator.onLine === false) throw new Error("Physical phase one must begin online");
  if (!/^host-[0-9a-f-]{16,128}$/u.test(hostInvocationId)) throw new Error("Host invocation ID is invalid");
  const bridge = new bundle.module.PhysicalAppAuditBridge();
  let launched: Awaited<ReturnType<typeof bundle.module.launchExactPrimeMach1BrowserAppFromInstallation>> | null = null;
  try {
    launched = await bundle.module.launchExactPrimeMach1BrowserAppFromInstallation(
      exactLaunchOptions(
        bundle,
        installation,
        `physical-online-${crypto.randomUUID()}`,
        bridge.listener,
      ),
    );
    const envelope = await bundle.module.createResumablePhysicalPhase1({ launched, auditBridge: bridge });
    // createResumablePhysicalPhase1 has source-emitted Worker destruction and
    // disposed this exact app before it returns.
    assertExactLaunchDisposed(launched, "Resumable physical phase one");
    launched = null;
    const acceptance = await bundle.module.parseResumablePhysicalPhase1(envelope);
    const page = await observeCurrentPage(bundle.serviceWorkerBuildId);
    if (!page.crossOriginIsolated || !page.serviceWorker.controlled || page.navigatorOnline !== true) {
      throw new Error("Physical phase-one page is not an isolated controlled online document");
    }
    const nonce = bytesToHex(crypto.getRandomValues(new Uint8Array(16)));
    const descriptor: SitePhysicalPhase1Descriptor = {
      kind: "prime-mach1-site-physical-phase1",
      schemaVersion: 1,
      nonce,
      runId: acceptance.runId,
      hostInvocationId,
      createdAt: new Date().toISOString(),
      runtimeManifestSha256: bundle.manifestSha256,
      modelFileInventory: installation.files.map((file) => ({
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
      })),
      page,
      exactCheckpoint: structuredClone(acceptance.exactCheckpoint),
      acceptanceCheckpoint: {
        kind: envelope.kind,
        byteCount: envelope.descriptorByteCount,
        sha256: envelope.descriptorSha256,
        bytesBase64: base64(envelope.descriptorBytes),
      },
    };
    const persisted = await persistDescriptor(descriptor);
    return {
      descriptor,
      ...persisted,
      envelope: { ...envelope, descriptorBytes: Uint8Array.from(envelope.descriptorBytes) },
      acceptance,
      bundle,
      installation,
    };
  } catch (error) {
    const failures: unknown[] = [error];
    if (launched) {
      try {
        await disposeExactLaunch(launched, "Failed physical phase one");
      } catch (cleanupError) {
        failures.push(cleanupError);
      }
    }
    try {
      bridge.close();
    } catch (cleanupError) {
      failures.push(cleanupError);
    }
    if (failures.length > 1) {
      throw new AggregateError(failures, "Physical phase one failed and full-stack cleanup also failed");
    }
    throw error;
  }
}

export async function armPhysicalOfflineNavigation(
  committed: CommittedSitePhysicalPhase1,
): Promise<{ navigationUrl: string; cancel(): void }> {
  if (committed.descriptor.nonce !== committed.marker.nonce) throw new Error("Physical descriptor/commit nonce mismatch");
  const sw = await swMessage({
    type: "prime-mach1-physical-arm",
    nonce: committed.descriptor.nonce,
    descriptorSha256: committed.descriptorSha256,
    pageInstanceId: SITE_PAGE_INSTANCE_ID,
    performanceTimeOrigin: performance.timeOrigin,
  });
  if (sw.buildId !== committed.descriptor.page.serviceWorker.buildId) {
    throw new Error("Service worker changed between phase-one observation and physical arm");
  }
  const cleanupPagehide = pagehideSender(committed.descriptor.nonce, committed.descriptorSha256);
  const target = new URL(location.href);
  target.search = "";
  target.searchParams.set("physical_phase", "2");
  target.searchParams.set("nonce", committed.descriptor.nonce);
  let navigated = false;
  const navigate = () => {
    if (navigated || navigator.onLine !== false) return;
    navigated = true;
    location.assign(target.href);
  };
  globalThis.addEventListener("offline", navigate, { once: true });
  return {
    navigationUrl: target.href,
    cancel() {
      globalThis.removeEventListener("offline", navigate);
      cleanupPagehide();
    },
  };
}

async function fetchBoundAsset(pathname: string, nonce: string, expected?: ExactAssetRef): Promise<Uint8Array> {
  const target = new URL(pathname, location.href);
  target.searchParams.set("physical_nonce", nonce);
  const response = await fetch(target, { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) throw new Error(`Physical dependency fetch failed: ${pathname} (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (expected && (bytes.byteLength !== expected.byteCount || bytesToHex(sha256(bytes)) !== expected.sha256)) {
    throw new Error(`Physical dependency identity failed: ${pathname}`);
  }
  return bytes;
}

async function prefetchAssemblerDependencies(
  bundle: LoadedExactRuntimeBundle,
  nonce: string,
): Promise<{
  frozen: {
    primeTemplateGoldenBytes: Uint8Array;
    opsStandaloneReceiptBytes: Uint8Array;
    opsIntegrationReceiptBytes: Uint8Array;
  };
  artifacts: Map<string, Uint8Array>;
}> {
  const byPath = new Map(bundle.manifest.assets.map((asset) => [asset.path, asset]));
  const required = (pathname: string): ExactAssetRef => {
    const value = byPath.get(pathname);
    if (!value) throw new Error(`Runtime manifest omitted physical dependency ${pathname}`);
    return value;
  };
  const paths = {
    memory64Js: "/runtime/wllama.js",
    memory64Wasm: "/runtime/wllama.wasm",
    memory32Js: "/runtime-compat/wllama.js",
    memory32Wasm: "/runtime-compat/wllama.wasm",
    standalone: "/runtime/receipts/standalone-conformance.json",
    integration: "/runtime/receipts/integration-build.json",
    primeTemplate: "/runtime/receipts/prime-template-minja-golden.json",
    chatTemplate: "/runtime/model/chat-template.jinja",
  } as const;
  const [
    modelManifest,
    tensorIndex,
    chatTemplate,
    memory64Js,
    memory64Wasm,
    memory32Js,
    memory32Wasm,
    standalone,
    integration,
    primeTemplate,
  ] = await Promise.all([
    fetchBoundAsset("/model/manifest.json", nonce),
    fetchBoundAsset("/model/tensor-index.json", nonce),
    fetchBoundAsset(paths.chatTemplate, nonce, required(paths.chatTemplate)),
    fetchBoundAsset(paths.memory64Js, nonce, required(paths.memory64Js)),
    fetchBoundAsset(paths.memory64Wasm, nonce, required(paths.memory64Wasm)),
    fetchBoundAsset(paths.memory32Js, nonce, required(paths.memory32Js)),
    fetchBoundAsset(paths.memory32Wasm, nonce, required(paths.memory32Wasm)),
    fetchBoundAsset(paths.standalone, nonce, required(paths.standalone)),
    fetchBoundAsset(paths.integration, nonce, required(paths.integration)),
    fetchBoundAsset(paths.primeTemplate, nonce, required(paths.primeTemplate)),
  ]);
  const exact = (bytes: Uint8Array, byteCount: number, hash: string, label: string): Uint8Array => {
    if (bytes.byteLength !== byteCount || bytesToHex(sha256(bytes)) !== hash) {
      throw new Error(`${label} physical dependency identity failed`);
    }
    return bytes;
  };
  const artifacts = new Map<string, Uint8Array>([
    ["models/mach1-webgpu-split/manifest.json", exact(modelManifest, 4_467, "9f068429675c3e92b0d6fa39cd177a487675487bfcc9532d746aaf6c1f1c5921", "model manifest")],
    ["models/mach1-webgpu-split/tensor-index.json", exact(tensorIndex, 441_942, "469206f990e5345e8670b2613d60f0ec007d6f7b180280d2922c8dec114e2383", "tensor index")],
    ["models/mach1-webgpu-split/chat-template.jinja", exact(chatTemplate, 7_764, "e84f32a23fdda27689f868aa4a1a5621f41133e51a48d7f3efcbea2839574259", "chat template")],
    ["mach1-wllama/build/wllama.js", memory64Js],
    ["mach1-wllama/build/wllama.wasm", memory64Wasm],
    ["mach1-wllama/build-compat/wllama.js", memory32Js],
    ["mach1-wllama/build-compat/wllama.wasm", memory32Wasm],
    ["exact-pwa-lab/mach1-webgpu-runtime/receipt/standalone-conformance.json", standalone],
    ["exact-pwa-lab/mach1-webgpu-runtime/receipt/integration-build.json", integration],
  ]);
  return {
    frozen: {
      primeTemplateGoldenBytes: primeTemplate,
      opsStandaloneReceiptBytes: standalone,
      opsIntegrationReceiptBytes: integration,
    },
    artifacts,
  };
}

export async function resumeResumableSitePhysicalAcceptance(
  nonce: string,
  onProgress?: (progress: ModelInstallProgress) => void,
  expected?: { runId: string; descriptorSha256: string },
): Promise<ResumedSitePhysicalAcceptance> {
  if (navigator.onLine !== false) throw new Error("Physical phase two refuses to boot while networking is enabled");
  if (globalThis.crossOriginIsolated !== true || !navigator.serviceWorker?.controller) {
    throw new Error("Physical phase two lost isolation or its service worker controller");
  }
  let launched: ExactOfflineLaunchedApp | null = null;
  let bridge: InstanceType<LoadedExactRuntimeBundle["module"]["PhysicalAppAuditBridge"]> | null = null;
  let verifier: Awaited<ReturnType<LoadedExactRuntimeBundle["module"]["WorkerPhysicalAcceptanceVerifier"]["create"]>> | null = null;
  let committed: CommittedSitePhysicalPhase1 | null = null;
  try {
    // This is the mandatory fresh 18-file OPFS reopen and full SHA pass.
    const installation = await openVerifiedMach1Installation(onProgress);
    const bundle = await loadExactRuntimeBundle(installation);
    committed = await readCommittedPhysicalPhase(nonce, bundle);
    if (
      (expected && (
        committed.descriptor.runId !== expected.runId ||
        committed.descriptorSha256 !== expected.descriptorSha256
      )) ||
      committed.descriptor.runtimeManifestSha256 !== bundle.manifestSha256 ||
      committed.descriptor.page.sitePageInstanceId === SITE_PAGE_INSTANCE_ID ||
      committed.descriptor.page.performanceTimeOrigin === performance.timeOrigin
    ) throw new Error("Physical phase two reused a page or changed the runtime manifest");
    const binding = await swMessage({
      type: "prime-mach1-physical-bind",
      nonce,
      descriptorSha256: committed.descriptorSha256,
      pageInstanceId: SITE_PAGE_INSTANCE_ID,
      performanceTimeOrigin: performance.timeOrigin,
      navigationEntries: navigationEntries(),
    });
    if (binding.buildId !== committed.descriptor.page.serviceWorker.buildId) {
      throw new Error("Physical phase two is controlled by a different service-worker build");
    }

    bridge = new bundle.module.PhysicalAppAuditBridge();
    launched = await bundle.module.launchExactPrimeMach1OfflinePageFromInstallation({
      ...exactLaunchOptions(bundle, installation, `physical-offline-${crypto.randomUUID()}`, bridge.listener, nonce),
      phase1: committed.acceptance.exactCheckpoint,
    });
    const page = await observeCurrentPage(bundle.serviceWorkerBuildId);
    if (
      page.navigatorOnline !== false || !page.crossOriginIsolated || !page.serviceWorker.controlled ||
      page.sitePageInstanceId === committed.descriptor.page.sitePageInstanceId
    ) throw new Error("Offline page observation did not prove a distinct disconnected document");

    // All verifier/runtime dependency bytes are fetched and hash-verified
    // before the service-worker audit is sealed. The assembler's later reads
    // are memory-only, so the terminal audit cannot hide network activity.
    const prefetched = await prefetchAssemblerDependencies(bundle, nonce);
    verifier = await bundle.module.WorkerPhysicalAcceptanceVerifier.create({
      manifestUrl: bundle.manifest.physicalVerifier.manifest.path,
      manifestByteCount: bundle.manifest.physicalVerifier.manifest.byteCount,
      manifestSha256: bundle.manifest.physicalVerifier.manifest.sha256,
    });
    const root = await navigator.storage.getDirectory();
    const sink = new bundle.module.OpfsPhysicalReleaseSink(root);
    let sealed: SealedPhysicalServiceWorkerAudit | null = null;
    const navigation = {
      proofKind: "site-sealed-origin-lockdown" as const,
      seal: async () => {
        if (sealed) throw new Error("Physical navigation audit was already sealed");
        sealed = await sealPhysicalServiceWorkerAudit(
          nonce,
          committed!.descriptorSha256,
          bundle.serviceWorkerBuildId,
        );
        return {
          kind: "prime-mach1-site-two-page-navigation-evidence" as const,
          schemaVersion: 1 as const,
          siteDescriptorBytes: Uint8Array.from(committed!.descriptorBytes),
          siteDescriptorByteCount: committed!.descriptorBytes.byteLength,
          siteDescriptorSha256: committed!.descriptorSha256,
          siteCommitMarkerBytes: Uint8Array.from(committed!.markerBytes),
          siteCommitMarkerByteCount: committed!.markerBytes.byteLength,
          siteCommitMarkerSha256: committed!.markerSha256,
          phase1Page: structuredClone(committed!.descriptor.page),
          phase2Page: structuredClone(page),
          exactPhase1: structuredClone(committed!.acceptance.exactCheckpoint),
          exactPhase2: structuredClone(launched!.phase2),
          serviceWorkerAuditBytes: Uint8Array.from(sealed.auditBytes),
          serviceWorkerAuditByteCount: sealed.auditByteCount,
          serviceWorkerAuditSha256: sealed.auditSha256,
        };
      },
      abortAndDisarm: async (reasonSha256: string) => {
        await abortPhysicalServiceWorkerAudit(nonce, reasonSha256);
      },
      disarmSealed: async () => {
        await disarmPhysicalServiceWorkerAudit(nonce);
      },
    };
    const result = await bundle.module.resumeResumablePhysicalAcceptance({
      phase1: committed.envelope,
      launched,
      auditBridge: bridge,
      navigation,
      assembler: {
        frozen: prefetched.frozen,
        runtimeObservation: async () => {
          const snapshot = launched!.runtime.getEvidenceSnapshot() as { memory?: unknown };
          if (!snapshot.memory) throw new Error("Live runtime memory observation is unavailable");
          const loaderReceipt = launched!.runtime.getPagerLoaderReceipt();
          return {
            proofKind: "live-runtime-memory-pager-observation",
            runtimeMemory: structuredClone(snapshot.memory),
            loaderReceipt: {
              path: loaderReceipt.path,
              bytes: Uint8Array.from(loaderReceipt.bytes),
            },
          };
        },
        runtimeArtifactReader: {
          proofKind: "physical-runtime-artifact-reader",
          read: async (pathname: string) => {
            const bytes = prefetched.artifacts.get(pathname);
            if (!bytes) throw new Error(`Physical runtime reader rejected unknown path ${pathname}`);
            return Uint8Array.from(bytes);
          },
        },
      },
      verifier,
      sink,
    });
    if (result.bundle.runId !== committed.descriptor.runId) {
      throw new Error("Physical resumable core returned the wrong release run ID");
    }
    // The core has committed the release marker and disarmed the sealed
    // service-worker audit before it resolves. Preserve only the small,
    // immutable phase-two identity needed by the host contract, then release
    // the full 7.33-GiB runtime before reporting success. A later run must
    // never inherit an orphaned model worker or WebGPU allocation.
    assertExactLaunchDisposed(launched, "Resumable physical phase two");
    const phase2 = structuredClone(launched.phase2);
    launched = null;
    verifier.dispose();
    verifier = null;
    bridge.close();
    bridge = null;
    return { committed, page, bundle, phase2, result };
  } catch (error) {
    const failures: unknown[] = [error];
    if (launched) {
      try {
        await disposeExactLaunch(launched, "Failed physical phase two");
      } catch (cleanupError) {
        failures.push(cleanupError);
      }
    }
    if (committed) {
      let statusFailed = false;
      let active: Awaited<ReturnType<typeof physicalServiceWorkerStatus>> = null;
      try {
        active = await physicalServiceWorkerStatus();
      } catch (cleanupError) {
        statusFailed = true;
        failures.push(cleanupError);
      }
      if (statusFailed || active?.terminal === "active") {
        try {
          await abortPhysicalServiceWorkerAudit(nonce, "site_phase2_wrapper_error");
        } catch (cleanupError) {
          failures.push(cleanupError);
        }
      }
    }
    try {
      verifier?.dispose();
    } catch (cleanupError) {
      failures.push(cleanupError);
    }
    try {
      bridge?.close();
    } catch (cleanupError) {
      failures.push(cleanupError);
    }
    if (failures.length > 1) {
      throw new AggregateError(failures, "Physical phase two failed and runtime/audit cleanup also failed");
    }
    throw error;
  }
}
